'use strict';

import { 
    CognitoIdentityProviderClient, 
    InitiateAuthCommand,
    RespondToAuthChallengeCommand,
    ConfirmSignUpCommand,
    ForgotPasswordCommand,
    ConfirmForgotPasswordCommand,
    ChangePasswordCommand,
    SignUpCommand,
    ResendConfirmationCodeCommand
} from "@aws-sdk/client-cognito-identity-provider";

import awsconfig from '../aws-exports';

// Cognito's InvalidPasswordException means the pool policy rejected the password; say which rule.
// Mirrors Helper/PasswordPolicy.elm policyMessage (and napFunction's message for the same error).
export const PASSWORD_POLICY_MESSAGE = 'Your password needs at least 14 characters, including an upper-case letter, a lower-case letter, a number and a symbol such as - ! @ #.';

// Custom error classes for consistent error handling
export class CognitoError extends Error {
    constructor(message, code, details) {
        super(message);
        this.name = 'CognitoError';
        this.code = code;
        this.details = details;
    }
}

export class MFARequiredError extends CognitoError {
    constructor(session, challengeName, username) {
        super('MFA required', 'MFA_REQUIRED');
        this.session = session;
        this.challengeName = challengeName;
        this.username = username;
    }
}

export class AuthenticationError extends CognitoError {
    constructor(message, code = 'AUTH_FAILED') {
        super(message, code);
        this.name = 'AuthenticationError';
    }
}

/**
 * Modern Cognito Service using AWS SDK v3
 * Replaces the legacy AWS SDK v2 implementation with clean, modern patterns
 */
export class CognitoService {
    constructor(region = awsconfig.aws_project_region) {
        this.client = new CognitoIdentityProviderClient({ region });
        this.clientId = awsconfig.aws_user_pools_web_client_id;
        this.userPoolId = awsconfig.aws_user_pools_id;
    }

    /**
     * Sign in user with username/password
     * Returns user session or MFA challenge
     */
    async signIn(username, password) {
        try {
            const command = new InitiateAuthCommand({
                AuthFlow: 'USER_PASSWORD_AUTH',
                ClientId: this.clientId,
                AuthParameters: {
                    USERNAME: username,
                    PASSWORD: password
                }
            });

            const result = await this.client.send(command);

            // Check if MFA is required
            if (result.ChallengeName) {
                throw new MFARequiredError(
                    result.Session, 
                    result.ChallengeName, 
                    username
                );
            }

            // Successful authentication
            if (result.AuthenticationResult) {
                return this._createUserSession(result.AuthenticationResult, username);
            }

            throw new AuthenticationError('Unexpected authentication result');

        } catch (error) {
            if (error instanceof MFARequiredError) {
                throw error; // Re-throw MFA errors as-is
            }
            
            // Handle AWS SDK errors
            if (error.name === 'NotAuthorizedException') {
                throw new AuthenticationError('Invalid username or password', 'INVALID_CREDENTIALS');
            } else if (error.name === 'UserNotConfirmedException') {
                throw new AuthenticationError('User account not confirmed', 'USER_NOT_CONFIRMED');
            } else if (error.name === 'UserNotFoundException') {
                throw new AuthenticationError('User not found', 'USER_NOT_FOUND');
            } else if (error.name === 'TooManyRequestsException') {
                throw new AuthenticationError('Too many failed attempts. Please try again later.', 'RATE_LIMITED');
            } else if (error.name === 'PasswordResetRequiredException') {
                throw new AuthenticationError('Password reset required', 'PASSWORD_RESET_REQUIRED');
            }

            // Generic error handling
            throw new AuthenticationError(
                error.message || 'Authentication failed', 
                'UNKNOWN_ERROR'
            );
        }
    }

    /**
     * Confirm MFA challenge (EMAIL_OTP, SMS_MFA, SOFTWARE_TOKEN_MFA)
     * No global state needed - session is passed as parameter
     */
    async confirmMFA(username, code, session, challengeName = 'EMAIL_OTP') {
        try {
            const command = new RespondToAuthChallengeCommand({
                ChallengeName: challengeName,
                ClientId: this.clientId,
                ChallengeResponses: {
                    [`${challengeName}_CODE`]: code,
                    USERNAME: username
                },
                Session: session
            });

            const result = await this.client.send(command);

            if (result.AuthenticationResult) {
                return this._createUserSession(result.AuthenticationResult, username);
            }

            // Check if additional challenges are required
            if (result.ChallengeName) {
                throw new MFARequiredError(
                    result.Session, 
                    result.ChallengeName, 
                    username
                );
            }

            throw new AuthenticationError('Unexpected MFA confirmation result');

        } catch (error) {
            if (error instanceof MFARequiredError) {
                throw error; // Re-throw MFA errors as-is
            }

            // Handle specific MFA errors
            if (error.name === 'CodeMismatchException') {
                throw new AuthenticationError('Invalid MFA code', 'INVALID_MFA_CODE');
            } else if (error.name === 'ExpiredCodeException') {
                throw new AuthenticationError('MFA code expired', 'MFA_CODE_EXPIRED');
            } else if (error.name === 'NotAuthorizedException') {
                throw new AuthenticationError('Invalid MFA code', 'INVALID_MFA_CODE');
            }

            // Generic error handling
            throw new AuthenticationError(
                error.message || 'MFA confirmation failed', 
                'MFA_FAILED'
            );
        }
    }

    /**
     * Sign up new user
     */
    async signUp(username, password, email, attributes = {}) {
        try {
            const command = new SignUpCommand({
                ClientId: this.clientId,
                Username: username,
                Password: password,
                UserAttributes: [
                    {
                        Name: 'email',
                        Value: email
                    },
                    ...Object.entries(attributes).map(([key, value]) => ({
                        Name: key,
                        Value: value
                    }))
                ]
            });

            const result = await this.client.send(command);
            return {
                userId: result.UserSub,
                userConfirmed: result.UserConfirmed,
                message: 'User registered successfully. Please check your email for confirmation.'
            };

        } catch (error) {
            if (error.name === 'UsernameExistsException') {
                throw new AuthenticationError('Username already exists', 'USERNAME_EXISTS');
            } else if (error.name === 'InvalidPasswordException') {
                throw new AuthenticationError(PASSWORD_POLICY_MESSAGE, 'INVALID_PASSWORD');
            } else if (error.name === 'InvalidParameterException') {
                throw new AuthenticationError('Invalid parameters provided', 'INVALID_PARAMETERS');
            }

            throw new AuthenticationError(
                error.message || 'User registration failed', 
                'SIGNUP_FAILED'
            );
        }
    }

    /**
     * Confirm user signup with confirmation code
     */
    async confirmSignUp(username, confirmationCode) {
        try {
            const command = new ConfirmSignUpCommand({
                ClientId: this.clientId,
                Username: username,
                ConfirmationCode: confirmationCode
            });

            await this.client.send(command);
            return { message: 'User confirmed successfully' };

        } catch (error) {
            if (error.name === 'CodeMismatchException') {
                throw new AuthenticationError('Invalid confirmation code', 'INVALID_CONFIRMATION_CODE');
            } else if (error.name === 'ExpiredCodeException') {
                throw new AuthenticationError('Confirmation code expired', 'CONFIRMATION_CODE_EXPIRED');
            } else if (error.name === 'NotAuthorizedException') {
                throw new AuthenticationError('User already confirmed', 'USER_ALREADY_CONFIRMED');
            }

            throw new AuthenticationError(
                error.message || 'User confirmation failed', 
                'CONFIRMATION_FAILED'
            );
        }
    }

    /**
     * Resend confirmation code
     */
    async resendConfirmationCode(username) {
        try {
            const command = new ResendConfirmationCodeCommand({
                ClientId: this.clientId,
                Username: username
            });

            await this.client.send(command);
            return { message: 'Confirmation code resent successfully' };

        } catch (error) {
            if (error.name === 'UserNotFoundException') {
                throw new AuthenticationError('User not found', 'USER_NOT_FOUND');
            } else if (error.name === 'InvalidParameterException') {
                throw new AuthenticationError('Invalid username', 'INVALID_USERNAME');
            }

            throw new AuthenticationError(
                error.message || 'Failed to resend confirmation code', 
                'RESEND_FAILED'
            );
        }
    }

    /**
     * Forgot password flow
     */
    async forgotPassword(username) {
        try {
            const command = new ForgotPasswordCommand({
                ClientId: this.clientId,
                Username: username
            });

            await this.client.send(command);
            return { message: 'Password reset code sent to your email' };

        } catch (error) {
            if (error.name === 'UserNotFoundException') {
                throw new AuthenticationError('User not found', 'USER_NOT_FOUND');
            } else if (error.name === 'InvalidParameterException') {
                throw new AuthenticationError('Invalid username', 'INVALID_USERNAME');
            }

            throw new AuthenticationError(
                error.message || 'Failed to initiate password reset', 
                'FORGOT_PASSWORD_FAILED'
            );
        }
    }

    /**
     * Confirm forgot password with reset code
     */
    async confirmForgotPassword(username, confirmationCode, newPassword) {
        try {
            const command = new ConfirmForgotPasswordCommand({
                ClientId: this.clientId,
                Username: username,
                ConfirmationCode: confirmationCode,
                Password: newPassword
            });

            await this.client.send(command);
            return { message: 'Password reset successfully' };

        } catch (error) {
            if (error.name === 'CodeMismatchException') {
                throw new AuthenticationError('Invalid reset code', 'INVALID_RESET_CODE');
            } else if (error.name === 'ExpiredCodeException') {
                throw new AuthenticationError('Reset code expired', 'RESET_CODE_EXPIRED');
            } else if (error.name === 'InvalidPasswordException') {
                throw new AuthenticationError(PASSWORD_POLICY_MESSAGE, 'INVALID_PASSWORD');
            }

            throw new AuthenticationError(
                error.message || 'Password reset failed', 
                'PASSWORD_RESET_FAILED'
            );
        }
    }

    /**
     * Change the signed-in user's password (the forced change after sign-in, ST4S A2 R4)
     */
    async changePassword(accessToken, previousPassword, proposedPassword) {
        try {
            const command = new ChangePasswordCommand({
                AccessToken: accessToken,
                PreviousPassword: previousPassword,
                ProposedPassword: proposedPassword
            });

            await this.client.send(command);
            return { message: 'Password changed successfully' };

        } catch (error) {
            if (error.name === 'InvalidPasswordException') {
                throw new AuthenticationError(PASSWORD_POLICY_MESSAGE, 'INVALID_PASSWORD');
            } else if (error.name === 'NotAuthorizedException') {
                throw new AuthenticationError('Your current password is not correct. Please try again, or sign out and use "Forgot password".', 'INVALID_CREDENTIALS');
            } else if (error.name === 'LimitExceededException' || error.name === 'TooManyRequestsException') {
                throw new AuthenticationError('Too many attempts. Please wait a while and try again.', 'RATE_LIMITED');
            }

            throw new AuthenticationError(
                'Sorry, we could not change your password. Please try again.',
                'CHANGE_PASSWORD_FAILED'
            );
        }
    }

    /**
     * Create user session from authentication result
     * Handles token storage in Amplify-compatible format
     */
    _createUserSession(authResult, username) {
        const { IdToken, AccessToken, RefreshToken } = authResult;
        
        // Store tokens in Amplify-compatible format for seamless integration
        const keyPrefix = `CognitoIdentityServiceProvider.${this.clientId}`;
        localStorage.setItem(`${keyPrefix}.${username}.idToken`, IdToken);
        localStorage.setItem(`${keyPrefix}.${username}.accessToken`, AccessToken);
        localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, RefreshToken);
        localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);

        // Parse JWT token to extract user information
        let userPayload = {};
        try {
            const tokenPayload = IdToken.split('.')[1];
            userPayload = JSON.parse(atob(tokenPayload));
        } catch (error) {
            console.warn('Failed to parse JWT token:', error);
        }

        // Return in Amplify-compatible format
        return {
            signInUserSession: {
                idToken: {
                    payload: {
                        email: userPayload.email || username,
                        sub: userPayload.sub || username,
                        'cognito:groups': userPayload['cognito:groups'] || []
                    }
                }
            },
            username: username
        };
    }
}
